// The GO4LEARN showcase site — prototype « Vitrine et tunnel v2 », 2026-09-14.
//
// ⚠️ No Turbo, no Stimulus, no Tailwind: a stylesheet written for these pages and a few
// gestures. Everything that matters (links, forms, FAQ in <details>, language in <details>)
// works WITHOUT this script; it adds the mobile drawer, the tabs and the home's quiz.
import "../stylesheets/learn_onboarding_fonts.css";
import "../stylesheets/go4learn_site.css";

const onReady = (fn) =>
  document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", fn) : fn();

onReady(() => {
  // ── Tiroir mobile ─────────────────────────────────────────────────────────
  const drawer = document.querySelector("[data-gls-drawer]");
  const openers = document.querySelectorAll("[data-gls-drawer-open]");
  const closeDrawer = () => {
    if (!drawer || drawer.hidden) return;
    drawer.hidden = true;
    document.body.classList.remove("gls-lock");
    openers.forEach((b) => b.setAttribute("aria-expanded", "false"));
    openers[0]?.focus();
  };
  const openDrawer = () => {
    if (!drawer) return;
    drawer.hidden = false;
    document.body.classList.add("gls-lock");
    openers.forEach((b) => b.setAttribute("aria-expanded", "true"));
    drawer.querySelector("[data-gls-drawer-close]")?.focus();
  };
  openers.forEach((b) => b.addEventListener("click", openDrawer));
  drawer?.querySelectorAll("[data-gls-drawer-close], a").forEach((el) =>
    el.addEventListener("click", closeDrawer)
  );

  // ── Menus <details> : un clic dehors ou Échap les referme ─────────────────
  const menus = document.querySelectorAll("details[data-gls-menu]");
  document.addEventListener("click", (e) => {
    menus.forEach((m) => { if (m.open && !m.contains(e.target)) m.open = false; });
  });
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    menus.forEach((m) => { m.open = false; });
    closeDrawer();
  });

  // ── The « Prévenez-moi » window of the waiting page (<dialog>) ──────────────
  // Without the script, the `#notify` link opens it through `:target`; with it, it is a
  // real modal that a click on the backdrop, the cross or Escape closes.
  document.querySelectorAll("dialog[data-gls-dialog]").forEach((dialog) => {
    if (typeof dialog.showModal !== "function") return;
    const close = () => dialog.close();
    document.querySelectorAll(`[data-gls-dialog-open="${dialog.id}"]`).forEach((opener) =>
      opener.addEventListener("click", (e) => { e.preventDefault(); dialog.showModal(); })
    );
    dialog.querySelectorAll("[data-gls-dialog-close]").forEach((el) =>
      el.addEventListener("click", (e) => { e.preventDefault(); close(); })
    );
    dialog.addEventListener("click", (e) => { if (e.target === dialog) close(); });
    if (dialog.hasAttribute("data-gls-dialog-start-open")) dialog.showModal();
  });

  // ── Onglets ───────────────────────────────────────────────────────────────
  document.querySelectorAll("[data-gls-tabs]").forEach((group) => {
    const tabs = group.querySelectorAll("[data-gls-tab]");
    const panels = group.querySelectorAll("[data-gls-panel]");
    const select = (key) => {
      tabs.forEach((t) => {
        const on = t.dataset.glsTab === key;
        t.setAttribute("aria-selected", String(on));
        // Roving tabindex (WAI-ARIA tabs): only the selected tab is in the Tab order.
        if (t.getAttribute("role") === "tab") t.tabIndex = on ? 0 : -1;
      });
      panels.forEach((p) => { p.hidden = p.dataset.glsPanel !== key; });
    };
    tabs.forEach((t, i) => {
      t.addEventListener("click", () => select(t.dataset.glsTab));
      // Arrows move between tabs, Home and End to the first and last (GO4TEACH lot 14 review).
      t.addEventListener("keydown", (event) => {
        const last = tabs.length - 1;
        const target = { ArrowRight: i === last ? 0 : i + 1, ArrowLeft: i === 0 ? last : i - 1, Home: 0, End: last }[event.key];
        if (target === undefined) return;
        event.preventDefault();
        tabs[target].focus();
        select(tabs[target].dataset.glsTab);
      });
    });
    // Sans script, tous les panneaux non masqués restent lisibles ; avec, seul l'onglet
    // marqué `aria-selected` s'affiche.
    const active = [...tabs].find((t) => t.getAttribute("aria-selected") === "true");
    if (active) select(active.dataset.glsTab);
  });

  // ── « Essayer maintenant »: the three-question quiz of the home ──────────
  // The server renders the first question with its correction in a <details> (readable
  // without this script); here the buttons answer, the live region announces the verdict,
  // and the next questions come from `data-questions` — the i18n, never this file.
  document.querySelectorAll("[data-gls-quiz]").forEach((root) => {
    let questions;
    let labels;
    try {
      questions = JSON.parse(root.dataset.questions || "[]");
      labels = JSON.parse(root.dataset.labels || "{}");
    } catch {
      return;
    }
    if (questions.length === 0) return;

    const $ = (name) => root.querySelector(`[data-gls-quiz-${name}]`);
    const question = $("question");
    const options = $("options");
    const feedback = $("feedback");
    const next = $("next");
    const result = $("result");
    const score = $("score");
    const progress = document.querySelector("[data-gls-quiz-progress]");
    const fill = (model, values) =>
      Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`%{${key}}`, value), model || "");
    const total = questions.length;
    let index = 0;
    let points = 0;
    let answered = false;

    $("reveal")?.remove();

    const show = (i) => {
      const q = questions[i];
      index = i;
      answered = false;
      $("chapter").textContent = q.chapter;
      $("count").textContent = fill(labels.counter, { number: i + 1, total, subject: q.subject, level: q.level });
      $("text").textContent = q.text;
      if (progress) progress.textContent = fill(labels.progress, { number: i + 1, total, subject: q.subject });
      options.replaceChildren(
        ...q.options.map((label, k) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "gls-quiz__option";
          button.dataset.index = String(k);
          button.textContent = label;
          return button;
        })
      );
      feedback.replaceChildren();
      next.hidden = true;
    };

    options.addEventListener("click", (event) => {
      const button = event.target.closest("button[data-index]");
      if (!button || answered) return;
      answered = true;
      const q = questions[index];
      const picked = Number(button.dataset.index);
      const right = picked === q.answer;
      if (right) points += 1;
      options.querySelectorAll("button").forEach((b, k) => {
        b.setAttribute("aria-disabled", "true");
        if (k === q.answer) b.classList.add("gls-quiz__option--right");
        else if (k === picked) b.classList.add("gls-quiz__option--wrong");
      });
      const verdict = document.createElement("strong");
      verdict.className = right ? "gls-quiz__verdict--right" : "gls-quiz__verdict--wrong";
      verdict.textContent = right ? labels.right : labels.wrong;
      const explain = document.createElement("span");
      explain.textContent = q.explain;
      feedback.replaceChildren(verdict, explain);
      next.textContent = index < total - 1 ? labels.next : labels.finish;
      next.hidden = false;
    });

    next.addEventListener("click", () => {
      if (index < total - 1) {
        show(index + 1);
        options.querySelector("button")?.focus();
        return;
      }
      question.hidden = true;
      next.hidden = true;
      score.textContent = fill(labels.score, { score: points, total });
      result.hidden = false;
      score.focus();
    });

    $("restart")?.addEventListener("click", () => {
      points = 0;
      result.hidden = true;
      question.hidden = false;
      show(0);
      options.querySelector("button")?.focus();
    });

    show(0);
  });

  // ── FAQ : filtre par espace + recherche ──────────────────────────────────
  // Une seule liste, filtrée sur place : les onglets ne dupliquent pas les questions.
  // Rendre 149 items six fois pèserait six fois, et le JSON-LD `FAQPage` — qui lit la
  // même source que la page — verrait chaque question plusieurs fois.
  //
  // ⚠️ La barre d'outils naît `hidden` : sans ce script elle ne s'affiche jamais, et la
  // liste reste ENTIÈRE. Un champ de recherche qui ne cherche pas est pire qu'absent.
  const deburr = (s) => {
    const t = (s || "").toLowerCase();
    // ⚠️ La recherche est INSENSIBLE AUX ACCENTS : on tape « echeance », pas « échéance ».
    try { return t.normalize("NFD").replace(/\p{Diacritic}/gu, ""); } catch { return t; }
  };

  document.querySelectorAll("[data-gls-faq]").forEach((root) => {
    const tools = root.querySelector("[data-gls-faq-tools]");
    const input = root.querySelector("[data-gls-faq-search]");
    const clear = root.querySelector("[data-gls-faq-clear]");
    const count = root.querySelector("[data-gls-faq-count]");
    const empty = root.querySelector("[data-gls-faq-empty]");
    const filters = [...root.querySelectorAll("[data-gls-faq-filter]")];
    const items = [...root.querySelectorAll("[data-gls-faq-item]")].map((el) => ({
      el,
      cat: el.dataset.glsFaqItem,
      // Question ET réponse : « mobile money » n'est dans aucun intitulé, et c'est
      // pourtant ce qu'on tape.
      text: deburr(el.dataset.glsFaqText),
    }));
    if (items.length === 0) return;

    tools?.removeAttribute("hidden");
    let cat = "all";

    const apply = () => {
      const q = deburr(input?.value.trim());
      let shown = 0;
      items.forEach((it) => {
        const ok = (cat === "all" || it.cat === cat) && (q === "" || it.text.includes(q));
        it.el.hidden = !ok;
        // Une réponse dépliée puis masquée reviendrait dépliée : on referme.
        if (!ok) it.el.open = false;
        if (ok) shown += 1;
      });
      if (empty) empty.hidden = shown > 0;
      if (count) {
        const model = shown === 1 ? count.dataset.one : count.dataset.other;
        count.textContent = (model || "").replace("%{count}", String(shown));
      }
      if (clear) clear.hidden = q === "";
    };

    const selectCategory = (key) => {
      cat = key;
      filters.forEach((o) => o.setAttribute("aria-pressed", String(o.dataset.glsFaqFilter === key)));
    };

    filters.forEach((b) =>
      b.addEventListener("click", () => {
        selectCategory(b.dataset.glsFaqFilter);
        apply();
      })
    );
    input?.addEventListener("input", apply);
    input?.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || !input.value) return;
      input.value = "";
      apply();
    });
    clear?.addEventListener("click", () => {
      if (!input) return;
      input.value = "";
      input.focus();
      apply();
    });

    // Lien profond `/fr/faq#parent-pay` : on ouvre la question visée.
    //
    // ⚠️ **Le filtre et la recherche sont remis à zéro.** Sans ça, un lien envoyé au
    // support ouvrirait une question que le filtre courant MASQUE : la page sauterait
    // sur du vide, et on conclurait que le lien est mort.
    //
    // ⚠️ **`hashchange` autant que le chargement** : un lien vers la même page ne
    // recharge rien, et le script ne repasserait jamais.
    const openFromHash = () => {
      const target = decodeURIComponent(location.hash.slice(1));
      const aimed = target && items.find((it) => it.el.id === target);
      if (!aimed) return;
      selectCategory("all");
      if (input) input.value = "";
      apply();
      aimed.el.open = true;
      aimed.el.scrollIntoView({ block: "start" });
    };
    apply();
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
  });

  // ── Afficher le mot de passe ─────────────────────────────────────────────
  document.querySelectorAll("[data-gls-password-toggle]").forEach((btn) => {
    const input = document.getElementById(btn.getAttribute("aria-controls"));
    if (!input) return;
    btn.addEventListener("click", () => {
      const shown = input.type === "text";
      input.type = shown ? "password" : "text";
      btn.setAttribute("aria-pressed", String(!shown));
      btn.setAttribute("aria-label", shown ? btn.dataset.show : btn.dataset.hide);
    });
  });
});
